import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Download, Upload, Camera, Image as ImageIcon,
  MapPin, Clock, AlertCircle, FileText,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const severityVariant = (sev) => {
  switch ((sev || '').toLowerCase()) {
    case 'severe': case 'major': case 'critical': return 'destructive';
    case 'moderate': return 'default';
    case 'minor': return 'secondary';
    default: return 'outline';
  }
};

const AccidentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const [accident, setAccident] = useState(null);
  const [loading, setLoading] = useState(true);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [labelInput, setLabelInput] = useState('Damage');
  const fileRef = useRef(null);

  useEffect(() => {
    if (!id) return;
    (async () => {
      setLoading(true);
      await Promise.all([fetchAccident(), fetchPhotoUrls()]);
      setLoading(false);
    })();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAccident = async () => {
    try {
      const resp = await fetch(`${backendUrl}/api/accidents/${id}`, { credentials: 'include' });
      if (!resp.ok) throw new Error('Not found');
      const data = await resp.json();
      setAccident(data);
    } catch (err) {
      console.error(err);
      toast.error('Accident report not found');
      navigate('/app/accidents', { replace: true });
    }
  };

  const fetchPhotoUrls = async () => {
    try {
      const resp = await fetch(`${backendUrl}/api/accidents/${id}/photo-urls`, { credentials: 'include' });
      if (!resp.ok) { setPhotos([]); return; }
      const data = await resp.json();
      setPhotos(data.photos || []);
    } catch (err) {
      console.warn(err);
      setPhotos([]);
    }
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Photo must be under 10MB');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('label', labelInput || 'Damage');
      const resp = await fetch(`${backendUrl}/api/accidents/${id}/photos`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!resp.ok) throw new Error('Upload failed');
      toast.success('Photo added');
      await fetchPhotoUrls();
      await fetchAccident();
    } catch (err) {
      console.error(err);
      toast.error('Failed to upload photo');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleExportPdf = async () => {
    try {
      const resp = await fetch(`${backendUrl}/api/accidents/${id}/generate-pdf`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!resp.ok) throw new Error('PDF failed');
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `accident-${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (err) {
      console.error(err);
      toast.error('Failed to export PDF');
    }
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-4" data-testid="accident-detail-loading">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (!accident) return null;

  const formattedTime = accident.date_time
    ? new Date(accident.date_time).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
    : (accident.created_at
        ? new Date(accident.created_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
        : '—');

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6"
      data-testid="accident-detail-page"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/app/accidents')} data-testid="accident-detail-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight" data-testid="accident-detail-title">
              {accident.title || 'Accident Report'}
            </h1>
            <p className="text-sm text-muted-foreground">ID: {accident.report_id}</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={handleExportPdf} data-testid="accident-detail-export-pdf">
          <Download className="w-4 h-4 mr-1" /> PDF
        </Button>
      </div>

      {/* Summary */}
      <Card>
        <CardContent className="p-5 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            {accident.severity && (
              <Badge variant={severityVariant(accident.severity)} data-testid="accident-detail-severity">
                {accident.severity}
              </Badge>
            )}
            {accident.status && <Badge variant="outline">{accident.status}</Badge>}
            {accident.accident_type && <Badge variant="outline">{accident.accident_type}</Badge>}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-muted-foreground" />
              <span>{formattedTime}</span>
            </div>
            {accident.location && (
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-muted-foreground" />
                <span>{accident.location}</span>
              </div>
            )}
          </div>
          {accident.description && (
            <p className="text-sm whitespace-pre-wrap" data-testid="accident-detail-description">
              {accident.description}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Photos */}
      <Card>
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Camera className="w-4 h-4 text-primary" />
              <h2 className="font-medium">Photos ({photos.length})</h2>
            </div>
            <div className="flex items-center gap-2">
              <Input
                value={labelInput}
                onChange={(e) => setLabelInput(e.target.value)}
                placeholder="Label (e.g. Front damage)"
                className="w-48 h-9"
                data-testid="accident-detail-photo-label"
              />
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
                data-testid="accident-detail-photo-input"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="accident-detail-add-photo"
              >
                <Upload className="w-4 h-4 mr-1" />
                {uploading ? 'Uploading…' : 'Add photo'}
              </Button>
            </div>
          </div>

          {photos.length === 0 ? (
            <div className="border border-dashed rounded-md p-8 text-center text-sm text-muted-foreground flex flex-col items-center gap-2">
              <ImageIcon className="w-6 h-6" />
              No photos attached yet.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {photos.map((p, idx) => (
                <a
                  key={`${p.path}-${idx}`}
                  href={p.signed_url || '#'}
                  target="_blank"
                  rel="noreferrer"
                  className="block group relative overflow-hidden rounded-md border bg-muted aspect-square"
                  data-testid={`accident-detail-photo-${idx}`}
                >
                  {p.signed_url ? (
                    <img
                      src={p.signed_url}
                      alt={p.label || `Accident photo ${idx + 1}`}
                      className="w-full h-full object-cover transition-transform group-hover:scale-[1.03]"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">
                      Unavailable
                    </div>
                  )}
                  {p.label && (
                    <div className="absolute inset-x-0 bottom-0 bg-black/50 text-white text-xs px-2 py-1 truncate">
                      {p.label}
                    </div>
                  )}
                </a>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Other vehicles / witnesses summary */}
      {(accident.other_vehicles?.length > 0 || accident.witnesses?.length > 0) && (
        <Card>
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-primary" />
              <h2 className="font-medium">Other parties</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              {accident.other_vehicles?.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
                    Other vehicles ({accident.other_vehicles.length})
                  </p>
                  <ul className="list-disc ml-5 space-y-1">
                    {accident.other_vehicles.map((v, i) => (
                      <li key={i}>{[v.reg_plate, v.make, v.model].filter(Boolean).join(' · ')}</li>
                    ))}
                  </ul>
                </div>
              )}
              {accident.witnesses?.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
                    Witnesses ({accident.witnesses.length})
                  </p>
                  <ul className="list-disc ml-5 space-y-1">
                    {accident.witnesses.map((w, i) => (
                      <li key={i}>{[w.name, w.phone].filter(Boolean).join(' · ') || 'Unnamed witness'}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {!accident.description && !accident.location && photos.length === 0 && (
        <div className="text-sm text-muted-foreground flex items-center gap-2 justify-center">
          <AlertCircle className="w-4 h-4" /> This accident report is empty. Upload photos or edit the report to add details.
        </div>
      )}
    </motion.div>
  );
};

export default AccidentDetail;
